import React, { useMemo } from 'react'
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

const chatRemarkPlugins = [remarkGfm, remarkLimitDepth] as const

// ─────────────────────────────────────────────────────────────────────────────
//  Safety guard: cap markdown nesting depth before the mdast→hast conversion.
//  mdast-util-to-hast (and the React render after it) recurses once per nesting
//  level, so a sufficiently deep tree overflows the JS call stack with an
//  uncaught `RangeError: Maximum call stack size exceeded`, thrown during render
//  — which would tear down the whole view. User messages already render as plain
//  text (see UserMessage), but this guards every other surface that renders
//  markdown through here (assistant text, coder summaries, super-admin view).
//
//  200 is deliberately far from both bounds: real content essentially never
//  nests past a handful of levels (a tree depth under ~50), while the prod crash
//  came from a tree ~2000 deep. 200 is an order of magnitude above anything
//  legible yet an order of magnitude below the stack limit — we should never
//  actually hit it; it just stops us approaching the ceiling. Iterative DFS so
//  this pass itself can't overflow on a pathological tree.
// ─────────────────────────────────────────────────────────────────────────────
const MAX_MARKDOWN_DEPTH = 200
function remarkLimitDepth() {
  return (tree: unknown) => {
    const stack: Array<{ node: { children?: unknown[] }; depth: number }> = [
      { node: tree as { children?: unknown[] }, depth: 0 }
    ]
    while (stack.length > 0) {
      const { node, depth } = stack.pop()!
      if (!Array.isArray(node.children)) continue
      if (depth >= MAX_MARKDOWN_DEPTH) {
        // Drop everything below the cap; the truncated node renders as-is and
        // to-hast stops descending here.
        node.children = []
        continue
      }
      for (const child of node.children) {
        stack.push({ node: child as { children?: unknown[] }, depth: depth + 1 })
      }
    }
  }
}

// ─────────────────────────────────────────────────────────────────────────────
//  Chat-tuned safeguard: escape literal pipes that sit inside quoted/code spans
//  on table-row lines, so a stray `"|"` (e.g. an HL7 separator) doesn't add a
//  phantom column. Differs from the doc-strength `escapeQuotedTablePipes` in
//  `documents/DocMarkdown.tsx` by design:
//   - chat content never contains the doc-only constructs (base64 blobs,
//     reference-image defs, bare DataWeave), so no other rules belong here;
//   - chat streams char-by-char via `streamingMarkdown.ts`'s reveal hook, so
//     this runs on every tick — full row-count comparison would be cumulative
//     O(n²) over the stream. A regex pass over `|`-leading lines is O(n) with
//     a tiny constant and stays correct mid-stream (it only escapes content
//     INSIDE backticks or quotes, never a real column delimiter).
// ─────────────────────────────────────────────────────────────────────────────
function escapeQuotedPipesInTableLines(text: string): string {
  if (!text.includes('|')) return text
  return text.replace(/^[ \t]*\|.*$/gm, line => line.replace(/(["`])\|(["`])/g, '$1\\|$2'))
}

// Module-level component definitions keep react-markdown from remounting DOM
// elements as parent chat state changes.
function MdP({ children, ...props }: React.HTMLAttributes<HTMLParagraphElement>) {
  return (
    <p
      className='text-left leading-relaxed hyphens-auto [word-break:break-word] [overflow-wrap:anywhere]'
      {...props}
    >
      {children}
    </p>
  )
}
function MdUl({ children, ...props }: React.HTMLAttributes<HTMLUListElement>) {
  return (
    <ul
      className='list-disc pl-5 space-y-1 [word-break:break-word] [overflow-wrap:anywhere]'
      {...props}
    >
      {children}
    </ul>
  )
}
function MdOl({ children, ...props }: React.OlHTMLAttributes<HTMLOListElement>) {
  return (
    <ol
      className='list-decimal pl-5 space-y-1 [word-break:break-word] [overflow-wrap:anywhere]'
      {...props}
    >
      {children}
    </ol>
  )
}
function MdLi({ children, ...props }: React.LiHTMLAttributes<HTMLLIElement>) {
  return (
    <li
      className='leading-relaxed hyphens-auto py-0.5 [word-break:break-word] [overflow-wrap:anywhere]'
      {...props}
    >
      {children}
    </li>
  )
}
function MdStrong({ children, ...props }: React.HTMLAttributes<HTMLElement>) {
  return (
    <strong className='font-semibold text-gray-900' {...props}>
      {children}
    </strong>
  )
}
function MdCode({ children, className, ...props }: React.HTMLAttributes<HTMLElement>) {
  if (!className?.includes('language-')) {
    return (
      <code
        className='bg-gray-100 text-gray-800 px-1.5 py-0.5 rounded text-sm font-mono border'
        {...props}
      >
        {children}
      </code>
    )
  }
  return <code {...props}>{children}</code>
}
function MdPre({ children, ...props }: React.HTMLAttributes<HTMLPreElement>) {
  const codeElement = React.Children.toArray(children)[0]
  const isValid = React.isValidElement(codeElement)
  const rawText = isValid ? codeElement.props.children || '' : ''
  const codeText = typeof rawText === 'string' ? rawText.trim() : rawText
  const language = isValid
    ? codeElement.props.className?.replace('language-', '') || 'text'
    : 'text'
  const copyToClipboard = () => {
    navigator.clipboard?.writeText(String(codeText))
  }
  return (
    <div className='relative my-4 rounded-lg border border-gray-200 bg-gray-50 w-full max-w-full min-w-0 overflow-hidden'>
      <div className='flex items-center justify-between px-4 py-2 border-b border-gray-200 bg-gray-100 rounded-t-lg'>
        <span className='text-xs font-medium text-gray-600 uppercase'>{language}</span>
        <button
          onClick={copyToClipboard}
          className='text-xs text-gray-500 hover:text-gray-700 px-2 py-1 rounded hover:bg-gray-200 transition-colors'
          title='Copy'
        >
          Copy
        </button>
      </div>
      <div className='overflow-x-auto'>
        <pre
          className='p-4 text-sm font-mono leading-relaxed whitespace-pre-wrap break-words min-w-0'
          {...props}
        >
          <code className='text-gray-800 whitespace-pre-wrap break-words'>{codeText}</code>
        </pre>
      </div>
    </div>
  )
}
function MdH1({ children, ...props }: React.HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h1 className='text-xl font-bold mb-3 mt-4 first:mt-0 text-gray-900' {...props}>
      {children}
    </h1>
  )
}
function MdH2({ children, ...props }: React.HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h2 className='text-lg font-bold mb-3 mt-4 first:mt-0 text-gray-900' {...props}>
      {children}
    </h2>
  )
}
function MdH3({ children, ...props }: React.HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h3 className='text-base font-bold mb-2 mt-3 first:mt-0 text-gray-900' {...props}>
      {children}
    </h3>
  )
}
function MdH4({ children, ...props }: React.HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h4 className='text-sm font-bold mb-2 mt-3 first:mt-0 text-gray-900' {...props}>
      {children}
    </h4>
  )
}
function MdH5({ children, ...props }: React.HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h5 className='text-sm font-semibold mb-2 mt-2 first:mt-0 text-gray-900' {...props}>
      {children}
    </h5>
  )
}
function MdH6({ children, ...props }: React.HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h6 className='text-xs font-semibold mb-2 mt-2 first:mt-0 text-gray-900' {...props}>
      {children}
    </h6>
  )
}
function MdTable({ children, ...props }: React.TableHTMLAttributes<HTMLTableElement>) {
  return (
    <div className='overflow-x-auto my-4 w-full'>
      <table className='w-full border-collapse border border-gray-300 table-fixed' {...props}>
        {children}
      </table>
    </div>
  )
}
function MdThead({ children, ...props }: React.HTMLAttributes<HTMLTableSectionElement>) {
  return (
    <thead className='bg-gray-100' {...props}>
      {children}
    </thead>
  )
}
function MdTbody({ children, ...props }: React.HTMLAttributes<HTMLTableSectionElement>) {
  return <tbody {...props}>{children}</tbody>
}
function MdTr({ children, ...props }: React.HTMLAttributes<HTMLTableRowElement>) {
  return (
    <tr className='border-b border-gray-200' {...props}>
      {children}
    </tr>
  )
}
function MdTh({ children, ...props }: React.ThHTMLAttributes<HTMLTableCellElement>) {
  return (
    <th
      className='border border-gray-300 px-4 py-2 text-left font-semibold text-gray-900 bg-gray-50 break-words'
      {...props}
    >
      {children}
    </th>
  )
}
function MdTd({ children, ...props }: React.TdHTMLAttributes<HTMLTableCellElement>) {
  return (
    <td className='border border-gray-300 px-4 py-2 text-gray-800 break-words' {...props}>
      {children}
    </td>
  )
}
function MdImg({ src, alt, ...props }: React.ImgHTMLAttributes<HTMLImageElement>) {
  return (
    <img
      src={src}
      alt={alt}
      className='max-w-full h-auto rounded-lg my-3 border border-gray-200 object-contain'
      {...props}
    />
  )
}
function MdBlockquote({ children, ...props }: React.BlockquoteHTMLAttributes<HTMLQuoteElement>) {
  return (
    <blockquote
      className='border-l-4 border-tokens-status-link-1 pl-4 py-2 my-3 bg-tokens-status-link-base text-tokens-neutral-text-secondary italic'
      {...props}
    >
      {children}
    </blockquote>
  )
}
function MdHr(props: React.HTMLAttributes<HTMLHRElement>) {
  return <hr className='my-6 border-0 h-px bg-gray-300' {...props} />
}

const mdComponentsBase = {
  p: MdP,
  ul: MdUl,
  ol: MdOl,
  li: MdLi,
  strong: MdStrong,
  code: MdCode,
  pre: MdPre,
  h1: MdH1,
  h2: MdH2,
  h3: MdH3,
  h4: MdH4,
  h5: MdH5,
  h6: MdH6,
  table: MdTable,
  thead: MdThead,
  tbody: MdTbody,
  tr: MdTr,
  th: MdTh,
  td: MdTd,
  img: MdImg,
  blockquote: MdBlockquote,
  hr: MdHr
} as const

const ChatMarkdownInner: React.FC<{
  children: string
  className?: string
  onTaskLinkClick?: (taskId: number) => void
}> = ({ children, className = '', onTaskLinkClick }) => {
  const components = useMemo(() => {
    const MdA = ({
      href,
      children: linkChildren,
      ...props
    }: React.AnchorHTMLAttributes<HTMLAnchorElement> & { children?: React.ReactNode }) => {
      const match = href?.match(/^\/c\/([^/?]+)\?task_id=(.+)$/)
      if (match && onTaskLinkClick) {
        return (
          <button
            onClick={() => onTaskLinkClick(parseInt(match[2], 10))}
            className='text-blue-600 hover:text-blue-800 underline cursor-pointer bg-transparent border-none p-0 m-0 font-inherit'
            type='button'
          >
            {linkChildren}
          </button>
        )
      }
      return (
        <a
          href={href}
          className='text-blue-600 hover:text-blue-800 underline cursor-pointer'
          target='_blank'
          rel='noopener noreferrer'
          {...props}
        >
          {linkChildren}
        </a>
      )
    }
    return { ...mdComponentsBase, a: MdA }
  }, [onTaskLinkClick])

  return (
    <div className='[&>*+*]:mt-4 w-full max-w-full min-w-0 break-words overflow-hidden'>
      <Markdown
        className={`${className} w-full max-w-full min-w-0 break-words`}
        remarkPlugins={chatRemarkPlugins}
        components={components}
      >
        {escapeQuotedPipesInTableLines(children)}
      </Markdown>
    </div>
  )
}

export const ChatMarkdown = React.memo(
  ChatMarkdownInner,
  (prev, next) =>
    prev.children === next.children &&
    prev.onTaskLinkClick === next.onTaskLinkClick &&
    prev.className === next.className
)
